// Formatting, role/status labels, and session persistence — grouped in one file.

/* ---- access values (mirror the backend Access constants) ---- */
export const ROLES = ["SUPER_ADMIN", "ADMIN", "USER"];
export const STATUSES = ["ACTIVE", "PENDING", "SUSPENDED"];
export const AUDIENCES = ["ALL", "USERS", "ADMINS"];

export const prettyRole = (r) => ({ SUPER_ADMIN: "Super admin", ADMIN: "Admin", USER: "User" }[r] || r);
export const prettyStatus = (s) =>
  (s || "").replace("_", " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/* ---- formatting ---- */
export const fmtDate = (iso) =>
  !iso ? "—" : new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
export const fmtDateTime = (iso) =>
  !iso ? "—" : new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
export const initials = (name = "") =>
  name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

/* ---- session persistence (remember me = localStorage, else sessionStorage) ---- */
const KEY = "pa_session";
export const session = {
  save(rec, remember) {
    try {
      const raw = JSON.stringify(rec);
      if (remember) { localStorage.setItem(KEY, raw); sessionStorage.removeItem(KEY); }
      else { sessionStorage.setItem(KEY, raw); localStorage.removeItem(KEY); }
    } catch {}
  },
  load() {
    try { return JSON.parse(localStorage.getItem(KEY) || sessionStorage.getItem(KEY)); }
    catch { return null; }
  },
  clear() { try { localStorage.removeItem(KEY); sessionStorage.removeItem(KEY); } catch {} },
  rememberedEmail() {
    try { return localStorage.getItem("pa_email") || ""; } catch { return ""; }
  },
  setRememberedEmail(email) {
    try { email ? localStorage.setItem("pa_email", email) : localStorage.removeItem("pa_email"); } catch {}
  },
};

/**
 * Cross-component guard for unsaved work. A WorkView sets:
 *   workGuard.current = { dirty: boolean, save: async () => {...} }
 * and clears it (null) on unmount. Other parts of the app (e.g. switching
 * client/process) can check it to prompt "save your work first?".
 */
export const workGuard = { current: null };

// Global in-flight request counter — drives the app-wide loading overlay so it lasts exactly as long as the
// real network load takes (login landing, tab switches, pagination, "show more", filter changes, saves…).
// Components never touch this; api.js bumps it around every request.
export const netLoading = {
  n: 0,
  listeners: new Set(),
  bump(d) { this.n = Math.max(0, this.n + d); this.listeners.forEach((f) => { try { f(this.n); } catch {} }); },
  sub(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
};

// Single owner of the centered "tab switched" overlay. Any view can fire uiSwitch.trigger() the instant a
// tab/sub-view is clicked (synchronous → instant feedback), but ONLY App renders the overlay. This replaces
// the per-view `switching`/`filtering` overlays that used to paint a SECOND spinner on top of App's one
// (the offset double-spinner on tab switches). Overlapping triggers extend the window instead of stacking.
export const uiSwitch = {
  until: 0,
  listeners: new Set(),
  trigger(ms = 850) {
    this.until = Date.now() + ms;
    this.listeners.forEach((f) => { try { f(true); } catch {} });
    window.setTimeout(() => {
      // Only clear if no later trigger pushed the window out past now.
      if (Date.now() >= this.until) this.listeners.forEach((f) => { try { f(false); } catch {} });
    }, ms);
  },
  sub(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); },
};